/**
 * Single source of truth for the numbers used by Cascade 3PL demo
 * dashboards. Mirrors the values used in the chat scripts (Demo 1, 5)
 * so every surface — chat, insights, dashboards — tells the same story.
 *
 * Anchor: ~$165M annual revenue · 4 fulfillment centers · ~280 FTE ·
 * 68 DTC brand customers. See `sets/cascade-3pl/index.ts` description.
 */

// ---------------------------------------------------------------------------
// Network-level
// ---------------------------------------------------------------------------

export const NETWORK_WEEK = {
  openOrders: 134_200,
  forecastRevenue: 3_120_000,
  forecastContribution: 485_000,
  contributionPct: 15.6,
  brandsActive: 68,
  topTenBrandRevenuePct: 58,
  cumulativeContributionYTD: 11_200_000,
  contributionTargetYTD: 12_500_000,
  laborNeededHours: 5_378,
  laborFTCapacityHours: 5_320,
  pickerFteCount: 133,
  laborNetGapHours: 58, // positive = short · net week, after Thu+Fri longs offset Mon-Wed shorts
  contingentSlotsRecommended: 58,
  contingentSlotsCostEstimate: 7_500,
  ftUtilizationPctThisWeek: 91,
} as const

// ---------------------------------------------------------------------------
// Brand portfolio (mirrors Demo 1 Beat 1 PNL_QUEUE)
// ---------------------------------------------------------------------------

export type CascadeBrandRow = {
  id: string
  name: string
  openOrdersWeek: number
  revenueWeek: number
  marginPct: number
  marginPct6moAgo: number
  /** % of total network revenue this brand represents. */
  revenueSharePct: number
  status: 'good' | 'warning' | 'critical'
}

export const BRAND_ROWS: CascadeBrandRow[] = [
  {
    id: 'northwind',
    name: 'Northwind Apparel',
    openOrdersWeek: 28_400,
    revenueWeek: 441_000,
    marginPct: 7.2,
    marginPct6moAgo: 18.0,
    revenueSharePct: 14.1,
    status: 'critical',
  },
  {
    id: 'briar',
    name: 'Briar Coffee Roasters',
    openOrdersWeek: 18_200,
    revenueWeek: 289_000,
    marginPct: 17.5,
    marginPct6moAgo: 17.8,
    revenueSharePct: 9.3,
    status: 'good',
  },
  {
    id: 'trailmix',
    name: 'Trailmix Provisions',
    openOrdersWeek: 16_500,
    revenueWeek: 244_000,
    marginPct: 14.1,
    marginPct6moAgo: 14.3,
    revenueSharePct: 7.8,
    status: 'good',
  },
  {
    id: 'oakhill',
    name: 'OakHill Skincare',
    openOrdersWeek: 9_200,
    revenueWeek: 208_000,
    marginPct: 16.8,
    marginPct6moAgo: 15.2,
    revenueSharePct: 6.7,
    status: 'good',
  },
  {
    id: 'altitude',
    name: 'Altitude Supplements',
    openOrdersWeek: 11_400,
    revenueWeek: 184_000,
    marginPct: 12.9,
    marginPct6moAgo: 13.2,
    revenueSharePct: 5.9,
    status: 'good',
  },
  {
    id: 'halcyon',
    name: 'Halcyon Pet',
    openOrdersWeek: 7_800,
    revenueWeek: 159_000,
    marginPct: 14.5,
    marginPct6moAgo: 14.0,
    revenueSharePct: 5.1,
    status: 'good',
  },
  {
    id: 'fernweh',
    name: 'Fernweh Outdoors',
    openOrdersWeek: 5_200,
    revenueWeek: 135_000,
    marginPct: 8.4,
    marginPct6moAgo: 11.1,
    revenueSharePct: 4.3,
    status: 'warning',
  },
  {
    id: 'lumen',
    name: 'Lumen Candles',
    openOrdersWeek: 4_100,
    revenueWeek: 94_000,
    marginPct: 3.2,
    marginPct6moAgo: 8.6,
    revenueSharePct: 3.0,
    status: 'warning',
  },
]

// ---------------------------------------------------------------------------
// Northwind trajectory (6-month historical + projection)
// ---------------------------------------------------------------------------

export type TrajectoryPoint = {
  monthLabel: string
  marginPct: number
  /** True for projected months past today. */
  projected: boolean
}

export const NORTHWIND_TRAJECTORY: TrajectoryPoint[] = [
  { monthLabel: 'Nov', marginPct: 18.0, projected: false },
  { monthLabel: 'Dec', marginPct: 17.4, projected: false },
  { monthLabel: 'Jan', marginPct: 15.1, projected: false },
  { monthLabel: 'Feb', marginPct: 12.4, projected: false },
  { monthLabel: 'Mar', marginPct: 10.6, projected: false },
  { monthLabel: 'Apr', marginPct: 9.1, projected: false },
  { monthLabel: 'May', marginPct: 7.2, projected: false },
  { monthLabel: 'Jun', marginPct: 5.6, projected: true },
  { monthLabel: 'Jul', marginPct: 3.8, projected: true },
  { monthLabel: 'Aug', marginPct: 2.7, projected: true },
  { monthLabel: 'Sep', marginPct: 2.1, projected: true },
  { monthLabel: 'Oct', marginPct: 1.0, projected: true },
  { monthLabel: 'Nov', marginPct: 0.0, projected: true },
]

// ---------------------------------------------------------------------------
// Mix-shift drivers (paired sparklines)
// ---------------------------------------------------------------------------

export type MixShiftSeriesPoint = {
  monthLabel: string
  value: number
}

export const ZONE7_SHARE_SERIES: MixShiftSeriesPoint[] = [
  { monthLabel: 'Nov', value: 12 },
  { monthLabel: 'Dec', value: 14 },
  { monthLabel: 'Jan', value: 16 },
  { monthLabel: 'Feb', value: 18 },
  { monthLabel: 'Mar', value: 22 },
  { monthLabel: 'Apr', value: 30 },
  { monthLabel: 'May', value: 35 },
]

export const PACKAGE_WEIGHT_SERIES: MixShiftSeriesPoint[] = [
  { monthLabel: 'Nov', value: 1.4 },
  { monthLabel: 'Dec', value: 1.42 },
  { monthLabel: 'Jan', value: 1.48 },
  { monthLabel: 'Feb', value: 1.56 },
  { monthLabel: 'Mar', value: 1.66 },
  { monthLabel: 'Apr', value: 1.81 },
  { monthLabel: 'May', value: 1.9 },
]

export const SHIP_COST_SERIES: MixShiftSeriesPoint[] = [
  { monthLabel: 'Nov', value: 7.2 },
  { monthLabel: 'Dec', value: 7.4 },
  { monthLabel: 'Jan', value: 7.95 },
  { monthLabel: 'Feb', value: 8.6 },
  { monthLabel: 'Mar', value: 9.65 },
  { monthLabel: 'Apr', value: 11.05 },
  { monthLabel: 'May', value: 11.85 },
]

// ---------------------------------------------------------------------------
// Labor — facility-level summary (Demo 5 Beat 1 mirror)
// ---------------------------------------------------------------------------

export type FacilityLabor = {
  id: string
  name: string
  ordersWeek: number
  laborNeededHours: number
  ftCapacityHours: number
  gapHours: number // positive = short, negative = long
  trabaSlots: number
  status: 'critical' | 'warning' | 'good'
  recommendation: string
}

/**
 * Ordered by gross-shortfall severity (largest absolute shortage Mon+Tue
 * first, then long facilities). Matches the chat-replay NETWORK_GRID row
 * order in `scripts/laborForecast.ts`.
 *
 * `gapHours` is the *net* weekly gap (Mon-Fri sum). It equals the sum of
 * each facility's `HEATMAP_CELLS` cells for week 1 — operationally, the
 * Mon-Wed gross shortage is what drives the Traba slot recommendation,
 * not this net number.
 */
export const FACILITY_LABOR: FacilityLabor[] = [
  {
    id: 'atl',
    name: 'Atlanta',
    ordersWeek: 38_200,
    laborNeededHours: 1_530,
    ftCapacityHours: 1_400,
    gapHours: 130,
    trabaSlots: 20,
    status: 'critical',
    recommendation: '20 Traba slots Mon+Tue',
  },
  {
    id: 'indy',
    name: 'Indianapolis HQ',
    ordersWeek: 61_200,
    laborNeededHours: 2_448,
    ftCapacityHours: 2_400,
    gapHours: 48,
    trabaSlots: 38,
    status: 'critical',
    recommendation: '38 Traba slots Mon–Wed',
  },
  {
    id: 'clt',
    name: 'Charlotte',
    ordersWeek: 17_200,
    laborNeededHours: 680,
    ftCapacityHours: 720,
    gapHours: -40,
    trabaSlots: 0,
    status: 'warning',
    recommendation: 'Release 5 FT or batch ahead',
  },
  {
    id: 'rno',
    name: 'Reno',
    ordersWeek: 17_600,
    laborNeededHours: 720,
    ftCapacityHours: 800,
    gapHours: -80,
    trabaSlots: 0,
    status: 'warning',
    recommendation: 'Release 10 FT or batch ahead',
  },
]

// ---------------------------------------------------------------------------
// Labor — 14-day forward heatmap (4 facilities × 10 working days)
//
// Each cell value = labor gap in hours for that facility/day.
// Positive = short (needs contingent or longer FT shifts), negative = long.
// Week 1 (May 25–29) matches the Demo 5 Beat 1 daily grid exactly for Indy
// and is roughly proportional for Atlanta. Week 2 is the forecast carried
// forward — Memorial Day weekend bleed-over softens by Wed of W2.
// ---------------------------------------------------------------------------

export type HeatmapCell = {
  facilityId: string
  dateLabel: string // "Mon 5/25"
  gapHours: number
}

export const HEATMAP_DAYS: string[] = [
  'Mon 5/25',
  'Tue 5/26',
  'Wed 5/27',
  'Thu 5/28',
  'Fri 5/29',
  'Mon 6/1',
  'Tue 6/2',
  'Wed 6/3',
  'Thu 6/4',
  'Fri 6/5',
]

export const HEATMAP_CELLS: HeatmapCell[] = [
  // Indianapolis HQ
  { facilityId: 'indy', dateLabel: 'Mon 5/25', gapHours: 88 },
  { facilityId: 'indy', dateLabel: 'Tue 5/26', gapHours: 112 },
  { facilityId: 'indy', dateLabel: 'Wed 5/27', gapHours: 20 },
  { facilityId: 'indy', dateLabel: 'Thu 5/28', gapHours: -60 },
  { facilityId: 'indy', dateLabel: 'Fri 5/29', gapHours: -112 },
  { facilityId: 'indy', dateLabel: 'Mon 6/1', gapHours: 54 },
  { facilityId: 'indy', dateLabel: 'Tue 6/2', gapHours: 38 },
  { facilityId: 'indy', dateLabel: 'Wed 6/3', gapHours: -12 },
  { facilityId: 'indy', dateLabel: 'Thu 6/4', gapHours: -28 },
  { facilityId: 'indy', dateLabel: 'Fri 6/5', gapHours: -45 },

  // Atlanta
  { facilityId: 'atl', dateLabel: 'Mon 5/25', gapHours: 72 },
  { facilityId: 'atl', dateLabel: 'Tue 5/26', gapHours: 58 },
  { facilityId: 'atl', dateLabel: 'Wed 5/27', gapHours: 18 },
  { facilityId: 'atl', dateLabel: 'Thu 5/28', gapHours: -10 },
  { facilityId: 'atl', dateLabel: 'Fri 5/29', gapHours: -8 },
  { facilityId: 'atl', dateLabel: 'Mon 6/1', gapHours: 22 },
  { facilityId: 'atl', dateLabel: 'Tue 6/2', gapHours: 18 },
  { facilityId: 'atl', dateLabel: 'Wed 6/3', gapHours: 4 },
  { facilityId: 'atl', dateLabel: 'Thu 6/4', gapHours: -16 },
  { facilityId: 'atl', dateLabel: 'Fri 6/5', gapHours: -24 },

  // Charlotte
  { facilityId: 'clt', dateLabel: 'Mon 5/25', gapHours: 12 },
  { facilityId: 'clt', dateLabel: 'Tue 5/26', gapHours: 8 },
  { facilityId: 'clt', dateLabel: 'Wed 5/27', gapHours: 0 },
  { facilityId: 'clt', dateLabel: 'Thu 5/28', gapHours: -22 },
  { facilityId: 'clt', dateLabel: 'Fri 5/29', gapHours: -38 },
  { facilityId: 'clt', dateLabel: 'Mon 6/1', gapHours: 4 },
  { facilityId: 'clt', dateLabel: 'Tue 6/2', gapHours: 2 },
  { facilityId: 'clt', dateLabel: 'Wed 6/3', gapHours: -8 },
  { facilityId: 'clt', dateLabel: 'Thu 6/4', gapHours: -14 },
  { facilityId: 'clt', dateLabel: 'Fri 6/5', gapHours: -22 },

  // Reno
  { facilityId: 'rno', dateLabel: 'Mon 5/25', gapHours: -8 },
  { facilityId: 'rno', dateLabel: 'Tue 5/26', gapHours: -12 },
  { facilityId: 'rno', dateLabel: 'Wed 5/27', gapHours: -10 },
  { facilityId: 'rno', dateLabel: 'Thu 5/28', gapHours: -22 },
  { facilityId: 'rno', dateLabel: 'Fri 5/29', gapHours: -28 },
  { facilityId: 'rno', dateLabel: 'Mon 6/1', gapHours: -4 },
  { facilityId: 'rno', dateLabel: 'Tue 6/2', gapHours: -2 },
  { facilityId: 'rno', dateLabel: 'Wed 6/3', gapHours: -18 },
  { facilityId: 'rno', dateLabel: 'Thu 6/4', gapHours: -24 },
  { facilityId: 'rno', dateLabel: 'Fri 6/5', gapHours: -32 },
]

// ---------------------------------------------------------------------------
// Indianapolis 5-day stacked breakdown
// ---------------------------------------------------------------------------

export type IndyDayStack = {
  dateLabel: string
  ftCapacityHours: number
  trabaContingentHours: number
  spareHours: number // negative implied capacity overhang; positive = unused FT room
}

export const INDY_STACK: IndyDayStack[] = [
  {
    dateLabel: 'Mon 5/25',
    ftCapacityHours: 480,
    trabaContingentHours: 88,
    spareHours: 0,
  },
  {
    dateLabel: 'Tue 5/26',
    ftCapacityHours: 480,
    trabaContingentHours: 112,
    spareHours: 0,
  },
  {
    dateLabel: 'Wed 5/27',
    ftCapacityHours: 480,
    trabaContingentHours: 20,
    spareHours: 0,
  },
  {
    dateLabel: 'Thu 5/28',
    ftCapacityHours: 480,
    trabaContingentHours: 0,
    spareHours: 60,
  },
  {
    dateLabel: 'Fri 5/29',
    ftCapacityHours: 480,
    trabaContingentHours: 0,
    spareHours: 112,
  },
]

// ---------------------------------------------------------------------------
// Indy Tuesday brand top 5 (mirrors Demo 5 Beat 2)
// ---------------------------------------------------------------------------

export type IndyTuesdayBrand = {
  id: string
  brand: string
  orders: number
  laborHours: number
  why: string
}

export const INDY_TUE_BRANDS: IndyTuesdayBrand[] = [
  {
    id: 'tue-trailmix',
    brand: 'Trailmix Provisions',
    orders: 3_200,
    laborHours: 128,
    why: 'Tuesday is consistently heavy',
  },
  {
    id: 'tue-briar',
    brand: 'Briar Coffee Roasters',
    orders: 2_800,
    laborHours: 112,
    why: 'Monday roast spillover',
  },
  {
    id: 'tue-northwind',
    brand: 'Northwind Apparel',
    orders: 2_400,
    laborHours: 116,
    why: 'West-coast surge · bulk-pickable',
  },
  {
    id: 'tue-oakhill',
    brand: 'OakHill Skincare',
    orders: 1_200,
    laborHours: 60,
    why: 'Multi-pick avg, slower per order',
  },
  {
    id: 'tue-altitude',
    brand: 'Altitude Supplements',
    orders: 1_400,
    laborHours: 56,
    why: 'On pace',
  },
]

// ---------------------------------------------------------------------------
// Traba history (last 8 weeks, slots posted at Indy + Atlanta combined)
// ---------------------------------------------------------------------------

export type TrabaHistoryPoint = {
  weekLabel: string
  slots: number
}

export const TRABA_HISTORY: TrabaHistoryPoint[] = [
  { weekLabel: 'W-8', slots: 22 },
  { weekLabel: 'W-7', slots: 31 },
  { weekLabel: 'W-6', slots: 18 },
  { weekLabel: 'W-5', slots: 24 },
  { weekLabel: 'W-4', slots: 42 },
  { weekLabel: 'W-3', slots: 35 },
  { weekLabel: 'W-2', slots: 28 },
  { weekLabel: 'W-1', slots: 49 },
]

export const TRABA_RECOMMENDED_THIS_WEEK = 58

// ---------------------------------------------------------------------------
// Ops Pulse — today vs forecast
// ---------------------------------------------------------------------------

export const OPS_TODAY = {
  ordersSoFar: 27_140,
  ordersForecast: 27_600,
  pacePct: 98,
  onTimeShipPctThisWeek: 96.8,
  onTimeShipPctLastWeek: 97.2,
  activeExceptionsCritical: 22,
  activeExceptionsTotal: 138,
  agentsActive: 8,
  agentsTotal: 8,
  failedRunsLast30Days: 0,
} as const

export type OpsFacilityToday = {
  id: string
  name: string
  ordersToday: number
  ordersForecast: number
  pacePct: number
  exceptionsCritical: number
  ftUtilizationPct: number
  status: 'good' | 'warning' | 'critical'
}

export const OPS_FACILITY_TODAY: OpsFacilityToday[] = [
  {
    id: 'indy',
    name: 'Indianapolis HQ',
    ordersToday: 13_200,
    ordersForecast: 13_800,
    pacePct: 96,
    exceptionsCritical: 6,
    ftUtilizationPct: 94,
    status: 'good',
  },
  {
    id: 'atl',
    name: 'Atlanta',
    ordersToday: 7_500,
    ordersForecast: 7_200,
    pacePct: 104,
    exceptionsCritical: 3,
    ftUtilizationPct: 91,
    status: 'good',
  },
  {
    id: 'rno',
    name: 'Reno',
    ordersToday: 3_800,
    ordersForecast: 3_500,
    pacePct: 109,
    exceptionsCritical: 5,
    ftUtilizationPct: 86,
    status: 'good',
  },
  {
    id: 'clt',
    name: 'Charlotte',
    ordersToday: 2_640,
    ordersForecast: 3_100,
    pacePct: 85,
    exceptionsCritical: 8,
    ftUtilizationPct: 78,
    status: 'critical',
  },
]

// ---------------------------------------------------------------------------
// Agent activity (this week)
// ---------------------------------------------------------------------------

export type AgentActivityRow = {
  id: string
  name: string
  facility: string
  runs: number
  output: string
  status: 'good' | 'warning'
}

export const AGENT_ACTIVITY: AgentActivityRow[] = [
  {
    id: 'trailmix',
    name: 'Trailmix bulk-ship',
    facility: 'Indy',
    runs: 5,
    output: '16,200 labels printed',
    status: 'good',
  },
  {
    id: 'briar',
    name: 'Briar Coffee bulk-ship',
    facility: 'Atlanta',
    runs: 5,
    output: '10,850 labels printed',
    status: 'good',
  },
  {
    id: 'northwind',
    name: 'Northwind bulk-ship',
    facility: 'Indy',
    runs: 5,
    output: '18,900 labels printed',
    status: 'good',
  },
  {
    id: 'oakhill',
    name: 'OakHill bulk-ship',
    facility: 'Reno',
    runs: 5,
    output: '6,800 labels printed',
    status: 'good',
  },
  {
    id: 'altitude',
    name: 'Altitude bulk-ship',
    facility: 'Charlotte',
    runs: 5,
    output: '6,100 labels printed',
    status: 'good',
  },
  {
    id: 'post-shipment',
    name: 'Post-shipment monitor',
    facility: 'Network',
    runs: 7,
    output: '285 exceptions · 220 notices · 38 escalations',
    status: 'good',
  },
  {
    id: 'claims',
    name: 'Carrier claims',
    facility: 'Network',
    runs: 5,
    output: '47 filed · $11,800 in motion',
    status: 'good',
  },
  {
    id: 'margin',
    name: 'Margin trajectory watch',
    facility: 'Network',
    runs: 7,
    output: '1 brand flagged · Northwind',
    status: 'warning',
  },
]

// ---------------------------------------------------------------------------
// Carrier performance (this week, network-wide)
// ---------------------------------------------------------------------------

export type CarrierPerfRow = {
  carrier: 'UPS' | 'USPS' | 'FedEx' | 'OnTrac'
  lossPct: number
  damagePct: number
  latePct: number
  /** Active eligible claims this week (matches Demo 4 carrier split). */
  claimsThisWeek: number
  /** Dollar amount in motion across those claims. */
  dollarsInMotion: number
}

/**
 * Per-carrier breakdown of claims FILED this week (47 total · $11,800 in
 * motion) — matches the carrier-claims agent's weekly output shown in
 * AGENT_ACTIVITY. Distinct from Demo 4's "28 eligible" queue, which is
 * what's ready to file next.
 */
export const CARRIER_PERFORMANCE: CarrierPerfRow[] = [
  {
    carrier: 'USPS',
    lossPct: 0.31,
    damagePct: 0.02,
    latePct: 0.8,
    claimsThisWeek: 22,
    dollarsInMotion: 4800,
  },
  {
    carrier: 'UPS',
    lossPct: 0.18,
    damagePct: 0.05,
    latePct: 1.2,
    claimsThisWeek: 14,
    dollarsInMotion: 4200,
  },
  {
    carrier: 'FedEx',
    lossPct: 0.14,
    damagePct: 0.07,
    latePct: 0.6,
    claimsThisWeek: 8,
    dollarsInMotion: 1800,
  },
  {
    carrier: 'OnTrac',
    lossPct: 0.08,
    damagePct: 0.02,
    latePct: 0.4,
    claimsThisWeek: 3,
    dollarsInMotion: 1000,
  },
]
